import {
	ADVERTISE_FLAT_PLANS,
	ADVERTISE_FREE_LAUNCH,
	getAdvertisePlan,
} from "@/lib/ads/pricing";
import {
	SHORTLIST_CANDIDATE_COUNT,
	SHORTLIST_PRICE_IDR,
	SHORTLIST_PRICE_USD_MINOR,
	SHORTLIST_SLA_HOURS,
} from "@/lib/billing/shortlist-offer";
import { getReportPlan } from "@/lib/reports/pricing";

/**
 * SEEKER MONETIZATION FREEZE — LIFTED 2026-09-09 by Kelvin (was standing
 * since 2026-07-03, reaffirmed by the 2026-08-06 growth/funnel/retention
 * audit, WS-E churn hygiene). A new `SeekerProductKey` / seeker-facing
 * paywall / pricing surface is ordinary work again — no gate in front of it.
 *
 * The freeze required W4 (week-4) signup-cohort retention >= 15% for at
 * least one full cohort before any new surface. It was lifted with that bar
 * still NOT met — re-probed 2026-09-09 (`pnpm ops:retention`): W4 across 5
 * measurable cohorts = 4/242 = 1.7% blended, best single cohort (2026-W28)
 * 2/53 = 3.8%. Lifting was Kelvin's decision, made with that number in
 * view, not an agent inference — see `docs/claude/monetization-freeze.md`
 * for the full rationale, the two earlier narrow carve-outs, and what a
 * future re-freeze should look like if retention regresses further.
 */
export type SeekerProductKey =
	| "pro_monthly"
	| "pro_quarterly"
	| "pro_annual"
	| "pro_annual_founding";

export type EmployerProductKey =
	| "employer_pilot_free"
	| "employer_standard"
	| "employer_featured"
	| "employer_premium";

export type ToolProductKey = "cv_review" | "salary_report";

export type DatasetProductKey = "dataset_full" | "dataset_commercial";

export type AdvertiserProductKey =
	| "advertiser_sidebar"
	| "advertiser_blog"
	| "advertiser_category"
	| "advertiser_bundle"
	| "advertiser_weekly"
	| "advertiser_monthly";

export type ReportProductKey = "report_single" | "report_bundle";

export type ApiProductKey = "api_starter" | "api_pro";

export type IntroPackProductKey =
	| "employer_intro_single"
	| "employer_intro_starter"
	| "employer_intro_growth"
	| "employer_intro_scale";

export type ProductKey = SeekerProductKey | EmployerProductKey;

export type ProductKind = "seeker" | "employer";

export type ProductConfig = {
	amountIdr: number;
	days: number;
	label: { id: string; en: string };
	kind: ProductKind;
};

export type ToolProductConfig = {
	amountIdr: number;
	credits: number;
	label: { id: string; en: string };
	kind: "tool";
};

export type DatasetProductConfig = {
	/** Indonesia price (Mayar QRIS), IDR. */
	amountIdr: number;
	/** Global display/Gumroad price in USD minor units (cents). */
	amountUsdMinor: number;
	/**
	 * Whether this tier's CSV includes the per-listing `source_url` column.
	 * Gated to the commercial (redistribution) tier — the lighter republication
	 * posture for the standard tier keeps parity with the anonymized reports.
	 * The download route reads this flag so the gating lives in one place.
	 */
	includesSourceUrl: boolean;
	label: { id: string; en: string };
	kind: "dataset";
};

export const PRODUCTS: Record<ProductKey, ProductConfig> = {
	pro_monthly: {
		amountIdr: 39_000,
		days: 30,
		label: { id: "Pro Bulanan", en: "Pro Monthly" },
		kind: "seeker",
	},
	// 3-month tier — only surfaced under the pricing-v2 flag (a new product, not a
	// reprice of an existing one). Its catalog amount IS the v2 price because it
	// has no "old" price to grandfather; PRICING_V2_LADDER mirrors it for the
	// new-cohort path. The grant logic reads only `days` (90), so this tier grants
	// 90 days of Pro through the same generic path as monthly/annual.
	pro_quarterly: {
		amountIdr: 129_000,
		days: 90,
		label: { id: "Pro 3 Bulan", en: "Pro 3-Month" },
		kind: "seeker",
	},
	pro_annual: {
		amountIdr: 299_000,
		days: 365,
		label: { id: "Pro Tahunan", en: "Pro Annual" },
		kind: "seeker",
	},
	pro_annual_founding: {
		amountIdr: 199_000,
		days: 365,
		label: { id: "Founding Annual", en: "Founding Annual" },
		kind: "seeker",
	},
	employer_pilot_free: {
		amountIdr: 0,
		days: 30,
		label: { id: "Pilot Gratis", en: "Free Pilot" },
		kind: "employer",
	},
	employer_standard: {
		amountIdr: 490_000,
		days: 30,
		label: { id: "Standard", en: "Standard" },
		kind: "employer",
	},
	employer_featured: {
		amountIdr: 1_990_000,
		days: 30,
		label: { id: "Featured", en: "Featured" },
		kind: "employer",
	},
	employer_premium: {
		amountIdr: 4_990_000,
		days: 60,
		label: { id: "Premium", en: "Premium" },
		kind: "employer",
	},
};

export const TOOL_PRODUCTS: Record<ToolProductKey, ToolProductConfig> = {
	cv_review: {
		amountIdr: 12_000,
		credits: 1,
		label: {
			id: "Skor ATS CV + Rencana Kerja",
			en: "ATS CV Score + Action Plan",
		},
		kind: "tool",
	},
	salary_report: {
		amountIdr: 15_000,
		credits: 1,
		label: {
			id: "Laporan Gaji & Tren Loker 2026",
			en: "Salary & Job Trends Report 2026",
		},
		kind: "tool",
	},
};

// ── Checkout order bump (T12, pricing-conversion-sweetspot-2026-07-21) ─────
// A cheap, near-zero-marginal-cost one-time add-on. Deliberately NOT a member
// of `TOOL_PRODUCTS`/`ToolProductKey` — its entitlement primitive is a
// `cv_review` tool-purchase row (same `toolPurchases` table, same
// `runCvReviewGeneration` AI-review + email pipeline, see the webhook's
// `decoded.kind === "bump"` branch), reusing the closest existing entitlement
// primitive instead of standing up a parallel results page/delivery flow.
export type BumpProductKey = "cv_deepdive_bump";

export type BumpProductConfig = {
	amountIdr: number;
	label: { id: string; en: string };
	kind: "bump";
};

export const BUMP_PRODUCTS: Record<BumpProductKey, BumpProductConfig> = {
	cv_deepdive_bump: {
		amountIdr: 19_000,
		label: {
			id: "Ulasan Mendalam CV",
			en: "CV Deep-Dive Review",
		},
		kind: "bump",
	},
};

// ── Self-serve Data Store (Phase 1) ────────────────────────────────────
// One-time purchase of the live, AI-enriched Indonesian tech-jobs dataset as
// CSV. Same rail as TOOL_PRODUCTS (one-time pay → entitlement row), but the
// entitlement gates an auth-checked download instead of an in-app credit.
//   - dataset_full       — the full active set, perpetual re-download.
//   - dataset_commercial — full set + redistribution license + `source_url`.
// (The "Pro / monthly refreshes" tier is deferred to Phase 2 — it needs refresh
// tracking; perpetual re-download covers the same need simply for v1.)
export const DATASET_PRODUCTS: Record<DatasetProductKey, DatasetProductConfig> =
	{
		dataset_full: {
			amountIdr: 149_000,
			amountUsdMinor: 900, // $9
			includesSourceUrl: false,
			label: {
				id: "Dataset Lengkap (CSV)",
				en: "Full Dataset (CSV)",
			},
			kind: "dataset",
		},
		dataset_commercial: {
			amountIdr: 1_500_000,
			amountUsdMinor: 9_500, // $95
			includesSourceUrl: true,
			label: {
				id: "Lisensi Komersial + Redistribusi",
				en: "Commercial + Redistribution License",
			},
			kind: "dataset",
		},
	};

// ── Self-serve Employer Intro Packs (Phase 2 — recruiter monetization) ──
// One-time purchase of N "warm intro" credits a recruiter spends to request a
// double-opt-in introduction to a matched candidate. The value metric is an
// *accepted intro* (outcome-aligned), NOT a job listing — this is the self-serve
// replacement for the manual pilot-review queue. Dual-rail like the dataset
// store: Mayar QRIS (IDR, Indonesia) + Gumroad (USD, global). A paid webhook
// tops up an isolated intro-credit wallet (mirrors src/lib/api/credits.ts);
// credits are perpetual (no expiry window). Per-intro cost falls as the pack
// grows. Prices are directional, founder-tunable anchors — tune, don't hardcode
// a gate around them (the flag is the sole switch).
export type IntroPackProductConfig = {
	/** Indonesia price (Mayar QRIS), IDR. */
	amountIdr: number;
	/** Global price (Gumroad), USD minor units (cents). */
	amountUsdMinor: number;
	/** Warm-intro credits granted per purchase. */
	credits: number;
	label: { id: string; en: string };
	kind: "intro_pack";
};

export const INTRO_PACK_PRODUCTS: Record<
	IntroPackProductKey,
	IntroPackProductConfig
> = {
	// Rp99rb single intro (2026-10-06 owner pricing: one intro Rp99rb, ten for
	// Rp499rb; replaces the 15-25%-of-salary headhunter fee). USD is the
	// Gumroad-rail anchor; there is no Gumroad listing for this key yet, so
	// global buyers fall through to Mayar QRIS (see buy-intro-pack.ts).
	employer_intro_single: {
		amountIdr: 99_000,
		amountUsdMinor: 900, // $9
		credits: 1,
		label: { id: "Satu Intro", en: "Single Intro" },
		kind: "intro_pack",
	},
	// Legacy SKU, kept resolvable so in-flight checkouts and old webhooks still
	// grant. Not listed publicly any more: at Rp149k/3 (Rp49.7k each) it is
	// cheaper per intro than `growth` (Rp49.9k each), so it would undercut the
	// 10-pack. See INTRO_PACK_LISTED_KEYS.
	employer_intro_starter: {
		amountIdr: 149_000,
		amountUsdMinor: 1_900, // $19
		credits: 3,
		label: { id: "Paket Intro Starter", en: "Intro Starter" },
		kind: "intro_pack",
	},
	employer_intro_growth: {
		amountIdr: 499_000, // was 390_000 until 2026-10-06 (owner pricing)
		amountUsdMinor: 4_900, // $49 (Gumroad listing price; unchanged)
		credits: 10,
		label: { id: "Paket Intro Growth", en: "Intro Growth" },
		kind: "intro_pack",
	},
	employer_intro_scale: {
		amountIdr: 990_000,
		amountUsdMinor: 11_900, // $119
		credits: 30,
		label: { id: "Paket Intro Scale", en: "Intro Scale" },
		kind: "intro_pack",
	},
};

const INTRO_PACK_KEYS: readonly IntroPackProductKey[] = [
	"employer_intro_single",
	"employer_intro_starter",
	"employer_intro_growth",
	"employer_intro_scale",
];

/**
 * The packs shown to buyers, in display order. `employer_intro_starter` stays
 * in INTRO_PACK_PRODUCTS (so old orders resolve) but is deliberately absent
 * here; UIs must iterate this list, never `Object.keys(INTRO_PACK_PRODUCTS)`.
 */
export const INTRO_PACK_LISTED_KEYS: readonly IntroPackProductKey[] = [
	"employer_intro_single",
	"employer_intro_growth",
	"employer_intro_scale",
];

export function isIntroPackProductKey(
	value: string,
): value is IntroPackProductKey {
	return (INTRO_PACK_KEYS as readonly string[]).includes(value);
}

// ── 72h Verified Shortlist (money-path W1) ─────────────────────────────
// A sourcing engagement: we return N verified, interested candidates against a
// role brief within an SLA. It grants NO job listing and publishes NOTHING.
//
// PRODUCT-IDENTITY DECISION — a SEPARATE key union + map, deliberately NOT a
// new member of `EmployerProductKey` / `PRODUCTS`. Three reasons, in the order
// they bite:
//
//  1. `ProductConfig` mandates `days`, a LISTING duration. A shortlist has no
//     listing, so any value would be a fiction — and not an inert one:
//     `grantEmployerOrder` multiplies `product.days` into the order's
//     `expiresAt`, so a made-up number becomes a real date on a real row.
//     What a shortlist actually has is a delivery SLA in HOURS, a different
//     unit with different arithmetic. Same-shaped ≠ same thing.
//  2. `PRODUCTS` membership is what makes a key SELECTABLE by the job-posting
//     checkout: `employer-checkout.ts` validates with `isEmployerProductKey`,
//     so joining the union would let a buyer send a shortlist key down a path
//     that demands a job description and stores a job payload.
//  3. Symmetrically, `decodeDescription` routes ANY `EmployerProductKey` to
//     `grantEmployerOrder`, whose first act on payment is to insert a
//     `job_submissions` row. Membership would silently point shortlist money
//     at the publish path — the exact failure this family exists to avoid.
//
// The cost of separating is one `decodeDescription` branch and one webhook
// dispatch branch (~30 lines). That is the same price `BUMP_PRODUCTS`,
// `DATASET_PRODUCTS`, `REPORT_PRODUCTS` and `INTRO_PACK_PRODUCTS` each paid,
// for the same reason: reusing the employer dispatch is only "free" when the
// grant at the end of it is the one you actually want.
//
// Prices come from `@/lib/billing/shortlist-offer` — the single source the
// outbound email template and the /employers/shortlist page already quote — so
// the numeral is never restated here. The import is one-directional (this file
// depends on that one, never the reverse), which keeps `shortlist-offer.ts`
// dependency-free for the outreach worker and the employers zone bundle.
//
// SCOPE: the one-off pilot only. The recurring subscription tier
// (`SHORTLIST_SUBSCRIPTION_*`) is a Mayar MEMBERSHIP product on a different
// webhook rail (`membership.*` events, src/lib/mayar/membership.ts); adding a
// key here that no grant path can settle would be a worse lie than the missing
// row this family fixes.
export type ShortlistProductKey = "shortlist_pilot";

export type ShortlistProductConfig = {
	/**
	 * The WHOLE shortlist price (Mayar QRIS), IDR major units. There is no
	 * success fee — see the PRICE STRUCTURE block in
	 * `src/lib/billing/shortlist-offer.ts` for why the deposit + fee pair was
	 * reversed on 2026-09-08.
	 */
	amountIdr: number;
	/** The whole price, USD minor units (cents). Not a Mayar charge. */
	amountUsdMinor: number;
	/** Verified, interested candidates delivered per shortlist. */
	candidateCount: number;
	/**
	 * Delivery SLA in HOURS — deliberately not `days`. This is a promise about
	 * when WE deliver, not a window during which the buyer's listing is live.
	 */
	slaHours: number;
	label: { id: string; en: string };
	kind: "shortlist";
};

export const SHORTLIST_PRODUCTS: Record<
	ShortlistProductKey,
	ShortlistProductConfig
> = {
	shortlist_pilot: {
		amountIdr: SHORTLIST_PRICE_IDR,
		amountUsdMinor: SHORTLIST_PRICE_USD_MINOR,
		candidateCount: SHORTLIST_CANDIDATE_COUNT,
		slaHours: SHORTLIST_SLA_HOURS,
		label: {
			id: "Shortlist Terverifikasi 72 Jam",
			en: "72h Verified Shortlist",
		},
		kind: "shortlist",
	},
};

const SHORTLIST_KEYS: readonly ShortlistProductKey[] = ["shortlist_pilot"];

export function isShortlistProductKey(
	value: string,
): value is ShortlistProductKey {
	return (SHORTLIST_KEYS as readonly string[]).includes(value);
}

// ── Self-serve Role Market Report products (Phase B3) ──────────────────
// One-time purchase of an auto-generated report + CSV. Amounts are sourced from
// REPORT_PLANS (src/lib/reports/pricing.ts) so the rate card and the Mayar
// checkout can never drift. IDR-only (Mayar QRIS). `report_single` carries a
// role at checkout (stored on the order row); `report_bundle` covers all roles.
export type ReportProductConfig = {
	amountIdr: number;
	label: { id: string; en: string };
	kind: "report";
};

export const REPORT_PRODUCTS: Record<ReportProductKey, ReportProductConfig> = {
	report_single: {
		amountIdr: getReportPlan("single")?.idrOnce ?? 449_000,
		label: { id: "Laporan Satu Peran", en: "Single Role Report" },
		kind: "report",
	},
	report_bundle: {
		amountIdr: getReportPlan("bundle")?.idrOnce ?? 1_290_000,
		label: { id: "Bundle Sektor", en: "Sector Bundle" },
		kind: "report",
	},
};

const REPORT_KEYS: readonly ReportProductKey[] = [
	"report_single",
	"report_bundle",
];

export function isReportProductKey(value: string): value is ReportProductKey {
	return (REPORT_KEYS as readonly string[]).includes(value);
}

// ── Self-serve job fair booth (owner ask 2026-10-31) ─────────────────────
// One booth at one virtual job fair. The PRICE is not a constant: it is the
// event's `fair_events.booth_price_idr` (an admin sets it per fair at
// /admin/fair), copied onto the pre-created `employer_orders` row (product_key
// `fair_booth`) and charged from that row. Order-keyed like advertiser/report:
// the webhook grant (src/lib/fair/self-serve.ts `grantFairBoothOrder`) flips
// the order to paid and the booth live in the same request.
export const FAIR_BOOTH_CHECKOUT_PRODUCT = {
	productKey: "fair_booth",
	label: { id: "Booth Job Fair Virtual", en: "Virtual Job Fair Booth" },
	kind: "fair_booth",
	priceSource: "fair_events.booth_price_idr",
} as const;

/** Mayar description for a fair booth order. Byte-distinct from every other
 * encoding (its own `fair_booth|orderId=` prefix, never the bare employer
 * `lokerdollar:<key>|orderId=` shape). */
export function encodeFairBoothDescription(orderId: string): string {
	return `${DESCRIPTION_PREFIX}fair_booth|orderId=${orderId}`;
}

// ── Own branded job fair (owner ask 2026-10-31) ──────────────────────────
// A company's own 3D hall, live for 30 days the moment payment succeeds.
// Owner: "want your own fair ... own fair x price" and, on the price,
// "split testing a / b high low". The price is one of two arms of the
// multivariate PostHog flag `fair-own-price` (src/lib/experiments/
// fair-own-price.ts); the arm shown is the arm charged, copied onto the
// pre-created `employer_orders` row (product_key `fair_own`) with the arm
// name, and the webhook (`fair_own|orderId=`) charges nothing new: it flips
// the order paid and the hall live (src/lib/fair/own-fair.ts).
export const FAIR_OWN_PRICE_IDR = {
	low: 4_990_000,
	high: 14_900_000,
} as const;
export type FairOwnPriceArm = keyof typeof FAIR_OWN_PRICE_IDR;
/** Days an own hall stays live after payment. */
export const FAIR_OWN_DAYS = 30;

export const FAIR_OWN_CHECKOUT_PRODUCT = {
	productKey: "fair_own",
	label: { id: "Job Fair Milik Perusahaan", en: "Your own job fair" },
	kind: "fair_own",
	priceSource: "flag fair-own-price (FAIR_OWN_PRICE_IDR)",
} as const;

/** Mayar description for an own-fair order (own `fair_own|orderId=` prefix). */
export function encodeFairOwnDescription(orderId: string): string {
	return `${DESCRIPTION_PREFIX}fair_own|orderId=${orderId}`;
}

// ── Retired Gumroad Data API plans (grant config only) ─────────────────
// Owner 2026-10-14: no in-app API checkout and no Gumroad for the API. Paid
// API volume is sold only on Apify (APIFY_STORE_URL, src/lib/api/urls.ts).
// These entries are NOT sellable products: nothing prices or links them. They
// stay so the Gumroad webhook can still extend or revoke a legacy subscriber's
// `api_subscriptions` row (`grantApiAccess({ tier, days })`) instead of letting
// that sale fall through to the seeker path. Tier names live in
// src/lib/api/tiers.ts.
export type ApiProductConfig = {
	/** The paid API tier this legacy plan grants (mirrors src/lib/api/tiers.ts). */
	tier: "starter" | "pro";
	/** Access window granted per legacy renewal (days). */
	days: number;
};

export const API_PRODUCTS: Record<ApiProductKey, ApiProductConfig> = {
	api_starter: { tier: "starter", days: 30 },
	api_pro: { tier: "pro", days: 30 },
};

const API_PRODUCT_KEYS: readonly ApiProductKey[] = ["api_starter", "api_pro"];

export function isApiProductKey(value: string): value is ApiProductKey {
	return (API_PRODUCT_KEYS as readonly string[]).includes(value);
}

// ── Managed AI-employee Platform subscriptions (B4) ────────────────────
// Recurring B2B plans for the managed AI-employee platform: a business subscribes
// and its `business_profiles` row (src/lib/platform/business-profiles.ts) gets the
// paid `planTier` + a monthly task-credit allotment (via src/lib/platform/grant.ts).
// DARK — no flag flip in this family; the grant path stays inert until a real
// platform product is purchased. Dual-rail like the dataset / intro-pack stores:
// Mayar QRIS (IDR, Indonesia) + Gumroad (USD, global). USD > IDR by design so
// USD-earning buyers cross-subsidize the low IDR home-market price.
//
// `free` is the default plan (no product, no credits) — only the three PAID tiers
// carry a product key. Credit allotments (80 / 250 / 800) come from the pricing
// table. This is a NEW isolated product family; it never touches the seeker
// `subscriptions` / API `api_subscriptions` economies.
export type PlatformProductKey =
	| "platform_starter"
	| "platform_pro"
	| "platform_scale";

/** All platform plan tiers, including the default free tier (which has no product). */
export type PlatformTier = "free" | "starter" | "pro" | "scale";

export type PlatformProductConfig = {
	/** The paid plan tier this product grants (mirrors business_profiles.planTier). */
	tier: Exclude<PlatformTier, "free">;
	/** Indonesia price (Mayar QRIS), IDR. */
	amountIdr: number;
	/** Global price (Gumroad), USD minor units (cents). */
	amountUsdMinor: number;
	/** Task credits granted (set, not incremented) per monthly billing cycle. */
	monthlyTaskCredits: number;
	/** Billing-cycle length granted per term (days). */
	days: number;
	label: { id: string; en: string };
	kind: "platform";
};

export const PLATFORM_PRODUCTS: Record<
	PlatformProductKey,
	PlatformProductConfig
> = {
	platform_starter: {
		tier: "starter",
		amountIdr: 299_000,
		amountUsdMinor: 4_900, // $49/mo
		monthlyTaskCredits: 80,
		days: 30,
		label: { id: "Platform Starter", en: "Platform Starter" },
		kind: "platform",
	},
	platform_pro: {
		tier: "pro",
		amountIdr: 899_000,
		amountUsdMinor: 19_900, // $199/mo
		monthlyTaskCredits: 250,
		days: 30,
		label: { id: "Platform Pro", en: "Platform Pro" },
		kind: "platform",
	},
	platform_scale: {
		tier: "scale",
		amountIdr: 2_900_000,
		amountUsdMinor: 59_900, // $599/mo
		monthlyTaskCredits: 800,
		days: 30,
		label: { id: "Platform Scale", en: "Platform Scale" },
		kind: "platform",
	},
};

const PLATFORM_PRODUCT_KEYS: readonly PlatformProductKey[] = [
	"platform_starter",
	"platform_pro",
	"platform_scale",
];

export function isPlatformProductKey(
	value: string,
): value is PlatformProductKey {
	return (PLATFORM_PRODUCT_KEYS as readonly string[]).includes(value);
}

/** The paid plan tier a platform product grants (starter | pro | scale). */
export function platformTierForProductKey(
	productKey: PlatformProductKey,
): Exclude<PlatformTier, "free"> {
	return PLATFORM_PRODUCTS[productKey].tier;
}

// ── Advertiser self-serve products (Phase 2) ───────────────────────────
// Display-ad sponsorships sold on /advertise. Prices are sourced from
// ADVERTISE_PLANS (src/lib/ads/pricing.ts) so the rate card and the Mayar
// checkout can never drift. IDR-only (Mayar QRIS); the thin global audience
// stays on the manual lead form. `slot` is the ad placement a paid campaign
// targets in Phase 3 fulfillment.
export type AdvertiserSlot =
	| "feed-sidebar"
	| "blog"
	| "category-footer"
	| "site-wide";

export type AdvertiserPlanKey =
	| "sidebar"
	| "blog"
	| "category"
	| "bundle"
	| "weekly"
	| "monthly";

export type AdvertiserProductConfig = {
	amountIdr: number;
	days: number;
	label: { id: string; en: string };
	kind: "advertiser";
	slot: AdvertiserSlot;
	/** Matching ADVERTISE_PLANS key (the rate-card row). */
	planKey: AdvertiserPlanKey;
};

export const ADVERTISER_PRODUCTS: Record<
	AdvertiserProductKey,
	AdvertiserProductConfig
> = {
	advertiser_sidebar: {
		amountIdr: getAdvertisePlan("sidebar")?.idrMonthly ?? 750_000,
		days: 30,
		label: { id: "Sidebar Feed", en: "Feed Sidebar" },
		kind: "advertiser",
		slot: "feed-sidebar",
		planKey: "sidebar",
	},
	advertiser_blog: {
		amountIdr: getAdvertisePlan("blog")?.idrMonthly ?? 500_000,
		days: 30,
		label: { id: "Sponsor Blog", en: "Blog Sponsor" },
		kind: "advertiser",
		slot: "blog",
		planKey: "blog",
	},
	advertiser_category: {
		amountIdr: getAdvertisePlan("category")?.idrMonthly ?? 400_000,
		days: 30,
		label: { id: "Footer Kategori", en: "Category Footer" },
		kind: "advertiser",
		slot: "category-footer",
		planKey: "category",
	},
	advertiser_bundle: {
		amountIdr: getAdvertisePlan("bundle")?.idrMonthly ?? 1_500_000,
		days: 30,
		label: { id: "Bundle Seluruh Situs", en: "Site-wide Bundle" },
		kind: "advertiser",
		slot: "site-wide",
		planKey: "bundle",
	},
	// Flat self-serve tiers (2026-10): one price, every placement rotates.
	advertiser_weekly: {
		amountIdr: ADVERTISE_FLAT_PLANS.weekly.idr,
		days: ADVERTISE_FLAT_PLANS.weekly.days,
		label: { id: "Iklan per minggu", en: "Weekly ad" },
		kind: "advertiser",
		slot: "site-wide",
		planKey: "weekly",
	},
	advertiser_monthly: {
		amountIdr: ADVERTISE_FLAT_PLANS.monthly.idr,
		days: ADVERTISE_FLAT_PLANS.monthly.days,
		label: { id: "Iklan per bulan", en: "Monthly ad" },
		kind: "advertiser",
		slot: "site-wide",
		planKey: "monthly",
	},
};

const ADVERTISER_KEYS: readonly AdvertiserProductKey[] = [
	"advertiser_sidebar",
	"advertiser_blog",
	"advertiser_category",
	"advertiser_bundle",
	"advertiser_weekly",
	"advertiser_monthly",
];

export function isAdvertiserProductKey(
	value: string,
): value is AdvertiserProductKey {
	return (ADVERTISER_KEYS as readonly string[]).includes(value);
}

/**
 * Free 14-day launch placement (flag `advertise-free-placement`). Deliberately
 * OUTSIDE `AdvertiserProductKey` / `ADVERTISER_PRODUCTS` (same reasoning as
 * `CPC_LEAD_PRODUCT_KEY`): `isAdvertiserProductKey` stays false for it, so the
 * Mayar/Gumroad catalogs, the webhook grant path, `encodeDescription` and the
 * manual-payment page can never see a zero-price key. Only
 * `createAdvertiserCheckout` accepts it, and it never calls a provider for it.
 */
export const ADVERTISER_FREE_LAUNCH_PRODUCT_KEY =
	"advertiser_free_launch" as const;

export type AdvertiserFreeLaunchProductKey =
	typeof ADVERTISER_FREE_LAUNCH_PRODUCT_KEY;

export const ADVERTISER_FREE_LAUNCH_PRODUCT = {
	amountIdr: ADVERTISE_FREE_LAUNCH.idr,
	days: ADVERTISE_FREE_LAUNCH.days,
	label: { id: "Iklan gratis 14 hari", en: "Free 14-day launch placement" },
	kind: "advertiser",
	slot: "site-wide",
} as const satisfies Omit<AdvertiserProductConfig, "planKey">;

/** Map a rate-card plan key (sidebar|blog|…) to its Mayar advertiser product. */
export function advertiserProductForPlan(
	planKey: AdvertiserPlanKey,
): AdvertiserProductKey {
	return `advertiser_${planKey}` as AdvertiserProductKey;
}

export const FOUNDING_LIMIT = 200;
export const FOUNDING_SOFT_CAP = 220;

const DESCRIPTION_PREFIX = "lokerdollar:";

const SEEKER_KEYS: readonly SeekerProductKey[] = [
	"pro_monthly",
	"pro_quarterly",
	"pro_annual",
	"pro_annual_founding",
];

const EMPLOYER_KEYS: readonly EmployerProductKey[] = [
	"employer_pilot_free",
	"employer_standard",
	"employer_featured",
	"employer_premium",
];

const TOOL_KEYS: readonly ToolProductKey[] = ["cv_review", "salary_report"];

const DATASET_KEYS: readonly DatasetProductKey[] = [
	"dataset_full",
	"dataset_commercial",
];

export const PILOT_LIMIT = 30;

const ALL_KEYS: readonly ProductKey[] = [...SEEKER_KEYS, ...EMPLOYER_KEYS];

export function isProductKey(value: string): value is ProductKey {
	return (ALL_KEYS as readonly string[]).includes(value);
}

export function isSeekerProductKey(value: string): value is SeekerProductKey {
	return (SEEKER_KEYS as readonly string[]).includes(value);
}

export function isEmployerProductKey(
	value: string,
): value is EmployerProductKey {
	return (EMPLOYER_KEYS as readonly string[]).includes(value);
}

export function isToolProductKey(value: string): value is ToolProductKey {
	return (TOOL_KEYS as readonly string[]).includes(value);
}

const BUMP_KEYS: readonly BumpProductKey[] = ["cv_deepdive_bump"];

export function isBumpProductKey(value: string): value is BumpProductKey {
	return (BUMP_KEYS as readonly string[]).includes(value);
}

export function isDatasetProductKey(value: string): value is DatasetProductKey {
	return (DATASET_KEYS as readonly string[]).includes(value);
}

// Seeker descriptions encode `userId`; employer descriptions encode the
// pre-created `employer_orders.id`. Both carry the productKey so the webhook
// can route to the correct grant path.
export function encodeDescription(
	productKey: SeekerProductKey,
	userId: string,
): string;
export function encodeDescription(
	productKey: EmployerProductKey,
	orderId: string,
): string;
export function encodeDescription(
	productKey: ToolProductKey,
	userId: string,
): string;
export function encodeDescription(
	productKey: DatasetProductKey,
	userId: string,
): string;
export function encodeDescription(
	productKey: AdvertiserProductKey,
	orderId: string,
): string;
export function encodeDescription(
	productKey: ReportProductKey,
	orderId: string,
): string;
export function encodeDescription(
	productKey: IntroPackProductKey,
	userId: string,
): string;
export function encodeDescription(
	productKey: BumpProductKey,
	userId: string,
): string;
export function encodeDescription(
	productKey: ShortlistProductKey,
	orderId: string,
): string;
export function encodeDescription(
	productKey:
		| ProductKey
		| ToolProductKey
		| DatasetProductKey
		| AdvertiserProductKey
		| ReportProductKey
		| IntroPackProductKey
		| BumpProductKey
		| ShortlistProductKey,
	subjectId: string,
): string {
	// Checked BEFORE the `isEmployerProductKey` fallback at the bottom: a
	// shortlist key must never take the bare `lokerdollar:<key>|orderId=` shape,
	// which `decodeDescription` resolves to `kind: "employer"` → the publish grant.
	if (isShortlistProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}shortlist|productKey=${productKey}|orderId=${subjectId}`;
	}
	if (isBumpProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}bump|productKey=${productKey}|userId=${subjectId}`;
	}
	if (isToolProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}tool|productKey=${productKey}|userId=${subjectId}`;
	}
	if (isDatasetProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}dataset|productKey=${productKey}|userId=${subjectId}`;
	}
	if (isAdvertiserProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}advertiser|productKey=${productKey}|orderId=${subjectId}`;
	}
	if (isReportProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}report|productKey=${productKey}|orderId=${subjectId}`;
	}
	if (isIntroPackProductKey(productKey)) {
		return `${DESCRIPTION_PREFIX}intro_pack|productKey=${productKey}|userId=${subjectId}`;
	}
	const field = isEmployerProductKey(productKey) ? "orderId" : "userId";
	return `${DESCRIPTION_PREFIX}${productKey}|${field}=${subjectId}`;
}

export type DecodedDescription =
	| { kind: "seeker"; productKey: SeekerProductKey; userId: string }
	| { kind: "employer"; productKey: EmployerProductKey; orderId: string }
	| { kind: "consultation"; requestId: string }
	| { kind: "tool"; productKey: ToolProductKey; userId: string }
	| { kind: "dataset"; productKey: DatasetProductKey; userId: string }
	| { kind: "advertiser"; productKey: AdvertiserProductKey; orderId: string }
	| { kind: "report"; productKey: ReportProductKey; orderId: string }
	| { kind: "intro_pack"; productKey: IntroPackProductKey; userId: string }
	| { kind: "bump"; productKey: BumpProductKey; userId: string }
	| { kind: "shortlist"; productKey: ShortlistProductKey; orderId: string }
	| { kind: "fair_booth"; orderId: string }
	| { kind: "fair_own"; orderId: string };

export function decodeDescription(
	description: string,
): DecodedDescription | null {
	if (!description.startsWith(DESCRIPTION_PREFIX)) return null;
	const rest = description.slice(DESCRIPTION_PREFIX.length);

	// F4: consultation encoding — separate prefix so seeker/employer decode is unchanged
	if (rest.startsWith("consultation|requestId=")) {
		const requestId = rest.slice("consultation|requestId=".length);
		if (!requestId) return null;
		return { kind: "consultation", requestId };
	}

	// Self-serve fair booth: fair_booth|orderId=xxx (price lives on the order).
	if (rest.startsWith("fair_booth|orderId=")) {
		const orderId = rest.slice("fair_booth|orderId=".length);
		if (!orderId || orderId.includes("|")) return null;
		return { kind: "fair_booth", orderId };
	}

	// Own branded fair: fair_own|orderId=xxx (price lives on the order).
	if (rest.startsWith("fair_own|orderId=")) {
		const orderId = rest.slice("fair_own|orderId=".length);
		if (!orderId || orderId.includes("|")) return null;
		return { kind: "fair_own", orderId };
	}

	// Order-bump payments (T12): bump|productKey=cv_deepdive_bump|userId=xxx
	if (rest.startsWith("bump|productKey=")) {
		const inner = rest.slice("bump|productKey=".length);
		const pipeIdx = inner.indexOf("|userId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const userId = inner.slice(pipeIdx + "|userId=".length);
		if (!productKey || !userId) return null;
		if (!isBumpProductKey(productKey)) return null;
		return { kind: "bump", productKey, userId };
	}

	// Tool payments: tool|productKey=cv_review|userId=xxx
	if (rest.startsWith("tool|productKey=")) {
		const inner = rest.slice("tool|productKey=".length);
		const pipeIdx = inner.indexOf("|userId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const userId = inner.slice(pipeIdx + "|userId=".length);
		if (!productKey || !userId) return null;
		if (!isToolProductKey(productKey)) return null;
		return { kind: "tool", productKey, userId };
	}

	// Dataset payments: dataset|productKey=dataset_full|userId=xxx
	if (rest.startsWith("dataset|productKey=")) {
		const inner = rest.slice("dataset|productKey=".length);
		const pipeIdx = inner.indexOf("|userId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const userId = inner.slice(pipeIdx + "|userId=".length);
		if (!productKey || !userId) return null;
		if (!isDatasetProductKey(productKey)) return null;
		return { kind: "dataset", productKey, userId };
	}

	// Advertiser payments: advertiser|productKey=advertiser_sidebar|orderId=xxx
	if (rest.startsWith("advertiser|productKey=")) {
		const inner = rest.slice("advertiser|productKey=".length);
		const pipeIdx = inner.indexOf("|orderId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const orderId = inner.slice(pipeIdx + "|orderId=".length);
		if (!productKey || !orderId) return null;
		if (!isAdvertiserProductKey(productKey)) return null;
		return { kind: "advertiser", productKey, orderId };
	}

	// Report payments: report|productKey=report_single|orderId=xxx
	if (rest.startsWith("report|productKey=")) {
		const inner = rest.slice("report|productKey=".length);
		const pipeIdx = inner.indexOf("|orderId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const orderId = inner.slice(pipeIdx + "|orderId=".length);
		if (!productKey || !orderId) return null;
		if (!isReportProductKey(productKey)) return null;
		return { kind: "report", productKey, orderId };
	}

	// 72h Verified Shortlist: shortlist|productKey=shortlist_pilot|orderId=xxx
	// Order-keyed (like advertiser/report), because the role brief the fulfilment
	// team works from lives on the pre-created `employer_orders` row.
	if (rest.startsWith("shortlist|productKey=")) {
		const inner = rest.slice("shortlist|productKey=".length);
		const pipeIdx = inner.indexOf("|orderId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const orderId = inner.slice(pipeIdx + "|orderId=".length);
		if (!productKey || !orderId) return null;
		if (!isShortlistProductKey(productKey)) return null;
		return { kind: "shortlist", productKey, orderId };
	}

	// Employer intro-pack payments: intro_pack|productKey=employer_intro_starter|userId=xxx
	if (rest.startsWith("intro_pack|productKey=")) {
		const inner = rest.slice("intro_pack|productKey=".length);
		const pipeIdx = inner.indexOf("|userId=");
		if (pipeIdx === -1) return null;
		const productKey = inner.slice(0, pipeIdx);
		const userId = inner.slice(pipeIdx + "|userId=".length);
		if (!productKey || !userId) return null;
		if (!isIntroPackProductKey(productKey)) return null;
		return { kind: "intro_pack", productKey, userId };
	}

	const [productPart, subjectPart] = rest.split("|");
	if (!productPart || !subjectPart) return null;
	if (!isProductKey(productPart)) return null;

	if (isEmployerProductKey(productPart)) {
		if (!subjectPart.startsWith("orderId=")) return null;
		const orderId = subjectPart.slice("orderId=".length);
		if (!orderId) return null;
		return { kind: "employer", productKey: productPart, orderId };
	}

	if (!subjectPart.startsWith("userId=")) return null;
	const userId = subjectPart.slice("userId=".length);
	if (!userId) return null;
	return { kind: "seeker", productKey: productPart, userId };
}

/** Encode a consultation payment description. Byte-distinct from seeker/employer. */
export function encodeConsultationDescription(requestId: string): string {
	return `${DESCRIPTION_PREFIX}consultation|requestId=${requestId}`;
}
